// packages/engine/src/decorations.ts
//
// Decorations — schema'd, named metamodel annotations written `@name(args)` on
// their own line above a declaration.
//
// The boundary against the two neighbouring mechanisms (SPEC §4.3, §4.4):
//   - an ASPECT is plane membership / classification — an open (key,value) pair
//     that weaves a node into a plane, cascades, and drives grouping/colouring;
//   - a FIELD is a value OF the element, part of its own type/form template;
//   - a DECORATION is a named annotation with a DECLARED SCHEMA, attachable
//     across many kinds of site — including sites the declaration grammar gives
//     no body at all, such as a process step. That clause is why a decoration
//     cannot just be a field.
//
// Two semantic tiers share one mechanism. PLAIN decorations are definition +
// display only. ENGINE-BOUND decorations (`planned` / `deprecated`) additionally
// carry lifecycle behaviour; they are seeded into the registry by the engine
// (see {@link SEEDED_DECORATIONS}) rather than imported, so they need no `use`
// and cannot go stale behind a stdlib bundle rebuild.

import type { Name, QualifiedName, SourceSpan, Value } from "./types.js";
import { ordinalOf, type Timeline } from "./timeline.js";

// =============================================================================
// Definition
// =============================================================================

/** The value types a decoration schema field may declare. Deliberately tiny and
 *  DECORATION-LOCAL: ordinary fields (§4.3) stay untyped blanks in this version,
 *  because typing them would interact with `required`, cascade/append (§4.6) and
 *  override/drop (§4.8). A decoration schema has none of those interactions.
 *
 *  `marker` is a point on the timeline. It is deliberately NOT called `date`:
 *  the value is whatever token the author writes — `2026-Q3`, `v3`, `1.5.2` —
 *  and its only meaning is its ORDER among the other markers in the model. */
export type DecorationFieldType = "marker" | "text" | "number" | "bool" | "ref" | "list";

export interface DecorationField {
  readonly name: Name;
  readonly type: DecorationFieldType;
  /** Element type of a `list of <t>`. */
  readonly of?: DecorationFieldType;
  readonly source?: SourceSpan;
  readonly nameSource?: SourceSpan;
}

/**
 * `decoration Name { targets: …; field: type … }`.
 *
 * `targets` is a RESERVED meta-field, never a schema field and never the
 * primary — a schema field named `targets` is a validation error.
 */
export interface DecorationDecl {
  readonly name: Name;
  /** Element kinds (and user type names) this decoration may attach to.
   *  Empty/absent = any element. */
  readonly targets?: readonly Name[];
  readonly fields: readonly DecorationField[];
  readonly descriptions?: readonly string[];
  /** Absent for engine-seeded definitions — they have no source. Hover shows
   *  built-in docs and go-to-definition is a no-op, exactly as it already
   *  behaves for builtin getters like `@connections`. */
  readonly source?: SourceSpan;
  readonly nameSource?: SourceSpan;
  readonly targetsSource?: SourceSpan;
  /** True for engine-seeded built-ins; redeclaring one is an error. */
  readonly builtin?: boolean;
}

// =============================================================================
// Instance (the `@name(args)` written above a declaration)
// =============================================================================

export interface DecorationArg {
  /** Absent for the single bare positional arg — it binds the primary field. */
  readonly name?: Name;
  readonly value: Value;
  readonly source?: SourceSpan;
  readonly nameSource?: SourceSpan;
}

export interface DecorationInstance {
  readonly name: Name;
  readonly args: readonly DecorationArg[];
  /** `@deco` / `@deco()` with no arguments at all. */
  readonly bare?: boolean;
  readonly source?: SourceSpan;
  readonly nameSource?: SourceSpan;
}

/**
 * Every element kind a decoration may declare as a target. `field` means a
 * top-level field of an element, not a field sub-member.
 *
 * Each entry must correspond to a site the walk actually yields — an
 * advertised kind that nothing produces is a target the author can name and
 * then never satisfy. `use` is absent because an import statement resolves to
 * no model node, `space` because a space is assigned per file rather than
 * declared as a decorable element, and `edge` because an edge is DERIVED: the
 * thing you annotate is the step that derives it, which is what `step` is for.
 */
export const DECORATION_TARGET_KINDS = [
  "module",
  "surface",
  "interface",
  "process",
  "subprocess",
  "type",
  "field",
  "aspect",
  "step",
] as const satisfies readonly Name[];

// =============================================================================
// Engine-bound lifecycle built-ins
// =============================================================================

/** Targets shared by both lifecycle decorations. Steps are absent on purpose —
 *  a step is model behaviour, not a lifecycle-bearing node — as are view-layer
 *  artifacts (`view`/`policy`/`query`/`test`). */
const LIFECYCLE_TARGETS: readonly Name[] = [
  "module",
  "surface",
  "interface",
  "process",
  "subprocess",
  "type",
  "field",
  "aspect",
];

export const PLANNED = "planned";
export const DEPRECATED = "deprecated";

/**
 * The two engine-bound definitions, seeded into every registry.
 *
 * They are deliberately NOT the same shape. Arriving is one event, so `planned`
 * carries a single `from`: the marker the element becomes live at. Leaving is
 * two, so `deprecated` carries the pair: `from` opens the sunset, `to` retires
 * it. In both the first declared field is the primary, so the single positional
 * always names WHERE THE TRANSITION STARTS — `@planned(v3)` arrives at v3,
 * `@deprecated(v3)` is deprecated from v3.
 */
export const SEEDED_DECORATIONS: readonly DecorationDecl[] = [
  {
    name: PLANNED,
    targets: LIFECYCLE_TARGETS,
    fields: [{ name: "from", type: "marker" }],
    descriptions: [
      "Planned but not yet built. Before `from` the element is planned (present " +
        "in to-be, absent from as-is); from `from` it is live. Written bare it is " +
        "planned with no date yet.",
    ],
    builtin: true,
  },
  {
    name: DEPRECATED,
    targets: LIFECYCLE_TARGETS,
    fields: [
      { name: "from", type: "marker" },
      { name: "to", type: "marker" },
      { name: "use", type: "ref" },
      { name: "reason", type: "text" },
    ],
    descriptions: [
      "On its way out. Before `from` the sunset is only announced; from `from` " +
        "the element is deprecated but still present; from `to` it is retired. " +
        "Written bare it is deprecated with no date yet.",
    ],
    builtin: true,
  },
];

export function isLifecycleDecoration(name: Name): boolean {
  return name === PLANNED || name === DEPRECATED;
}

/** The primary field of a definition — the first SCHEMA field (`targets` is a
 *  meta-field and never primary). A definition with no schema fields takes
 *  `description` as its primary, matching language-wide bare-string-is-
 *  description (§4.9), so `@note("free text")` works with an empty body. */
export function primaryFieldOf(decl: DecorationDecl): Name {
  return decl.fields[0]?.name ?? "description";
}

// =============================================================================
// Registry
// =============================================================================

/**
 * Name-keyed decoration definitions, mirroring {@link ResolvedTypeRegistry}'s
 * dual-map shape: `byId` is space-qualified so same-named decorations in
 * different spaces stay distinct, `kinds` is the name-keyed view a lookup falls
 * back to.
 */
export interface DecorationRegistry {
  readonly byId: ReadonlyMap<string, DecorationDecl>;
  readonly kinds: ReadonlyMap<string, DecorationDecl>;
}

export const EMPTY_DECORATION_REGISTRY: DecorationRegistry = {
  byId: new Map(),
  kinds: new Map(),
};

/**
 * Collect declared decorations into a registry, with the engine-bound
 * lifecycle built-ins seeded first.
 *
 * Seeding rather than importing is what lets `@planned` / `@deprecated` be
 * written with no `use` and with no dependency on a stdlib bundle rebuild.
 * The flip side is that they are CLOSED: redeclaring one is an error reported
 * by `onDuplicate`, not a silent shadow — engine-bound semantics must not be
 * redefinable out from under the timeline.
 */
export function buildDecorationRegistry(
  decls: readonly DecorationDecl[],
  opts: {
    readonly spaceOf?: (d: DecorationDecl) => QualifiedName | undefined;
    readonly onDuplicate?: (d: DecorationDecl, previous: DecorationDecl) => void;
  } = {},
): DecorationRegistry {
  const byId = new Map<string, DecorationDecl>();
  const kinds = new Map<string, DecorationDecl>();
  for (const b of SEEDED_DECORATIONS) {
    byId.set(decorationId(undefined, b.name), b);
    kinds.set(b.name, b);
  }
  for (const d of decls) {
    const space = opts.spaceOf?.(d);
    const id = decorationId(space, d.name);
    const clash = byId.get(id) ?? (kinds.get(d.name)?.builtin ? kinds.get(d.name) : undefined);
    if (clash) {
      opts.onDuplicate?.(d, clash);
      continue;
    }
    byId.set(id, d);
    // Root-space declarations win the bare-name view, mirroring the kind registry.
    if (!kinds.has(d.name) || id.startsWith("::")) kinds.set(d.name, d);
  }
  return { byId, kinds };
}

// =============================================================================
// Lifecycle state & windows
// =============================================================================

/**
 * Every lifecycle state, as values.
 *
 * The VALUES are the source and the type is derived from them, not the other
 * way round: `readonly LifecycleState[]` is an ordinary array annotation, so a
 * list that had drifted to three entries would still typecheck, and the
 * diagnostics below — which name states from this list — would silently stop
 * covering the missing one. Deriving the union makes the list exhaustive by
 * construction.
 */
export const LIFECYCLE_STATES = ["planned", "live", "deprecated", "retired"] as const;

/** An element's lifecycle state AT a scrub point. */
export type LifecycleState = (typeof LIFECYCLE_STATES)[number];

/**
 * States that are NOT also decoration names.
 *
 * `planned` and `deprecated` are both: the marker you write and the state it
 * puts the element in. `live` and `retired` are only ever derived — there is no
 * `@live` or `@retired` to write — which is exactly the confusion worth naming
 * in a diagnostic rather than letting an author declare a metadata decoration
 * that compiles and paints nothing.
 *
 * Derived from the seed rather than listed, so adding an engine-bound
 * decoration removes its name from here automatically.
 */
export const DERIVED_ONLY_LIFECYCLE_STATES: readonly LifecycleState[] = LIFECYCLE_STATES.filter(
  (s) => !SEEDED_DECORATIONS.some((d) => d.name === s),
);

/**
 * A resolved lifecycle window, in TIMELINE ORDINALS (see `timeline.ts`) — not
 * instants. Every bound is optional and each absence has a fixed reading:
 *
 * - `bornFrom` absent → the element was never planned; it has always been here.
 * - `deadFrom` absent → it was never deprecated.
 * - `deadTo` absent → the sunset has no retirement marker yet; it stays
 *   deprecated indefinitely.
 *
 * A BARE decoration is the same window with an infinite bound: `@planned` alone
 * is `bornFrom: +Infinity` (planned, no marker yet, so never live), `@deprecated`
 * alone is `deadFrom: -Infinity` (deprecated at every point). That keeps the
 * bare form inside the ordinary comparison and clamp arithmetic instead of
 * needing presence flags beside it.
 */
export interface LifecycleWindow {
  /** Arrival marker — from `@planned(from:)`. Live from here on. */
  readonly bornFrom?: number;
  /** Sunset opens — from `@deprecated(from:)`. */
  readonly deadFrom?: number;
  /** Retired — from `@deprecated(to:)`. */
  readonly deadTo?: number;
}

export const LIVE_WINDOW: LifecycleWindow = {};

/** True when this window says anything at all — an undecorated element is live
 *  at every scrub point and needs no per-node state. */
export function isDecoratedWindow(w: LifecycleWindow): boolean {
  return w.bornFrom !== undefined || w.deadFrom !== undefined || w.deadTo !== undefined;
}

/**
 * Resolve a window to its state at scrub point `t`.
 *
 * Death dominates birth: an element past its retirement is `retired` whatever
 * its arrival says, which is what makes a chained
 * `@planned(v1) … @deprecated(v5, v6)` read correctly across its whole life.
 */
export function lifecycleStateAt(w: LifecycleWindow, t: number): LifecycleState {
  if (w.deadTo !== undefined && t >= w.deadTo) return "retired";
  if (w.deadFrom !== undefined && t >= w.deadFrom) return "deprecated";
  if (w.bornFrom !== undefined && t < w.bornFrom) return "planned";
  return "live";
}

/** Whether a state means the element is absent from the AS-IS model at that
 *  point. Planned counts: it is agreed and dated, but not built yet. */
export function isAbsent(state: LifecycleState): boolean {
  return state === "planned" || state === "retired";
}

/**
 * Structural containment clamp (§3.4a of the plan) — a child cannot be present
 * when its container is not. Birth is pushed LATER by an ancestor born later;
 * death is pulled EARLIER by an ancestor that dies sooner.
 *
 * This is an invariant, not a preference: a child declaring a window WIDER than
 * its container is a modelling error reported by validation, not silently
 * narrowed here. This function computes the effective window for rendering and
 * query once that check has run.
 */
export function clampToContainer(
  child: LifecycleWindow,
  container: LifecycleWindow,
): LifecycleWindow {
  return {
    bornFrom: maxDefined(child.bornFrom, container.bornFrom),
    deadFrom: minDefined(child.deadFrom, container.deadFrom),
    deadTo: minDefined(child.deadTo, container.deadTo),
  };
}

/** An empty window: the element is gone before it ever arrives. */
export function isEmptyWindow(w: LifecycleWindow): boolean {
  const dead = w.deadTo ?? w.deadFrom;
  return w.bornFrom !== undefined && dead !== undefined && dead <= w.bornFrom;
}

function maxDefined(a: number | undefined, b: number | undefined): number | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return Math.max(a, b);
}

function minDefined(a: number | undefined, b: number | undefined): number | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return Math.min(a, b);
}

// =============================================================================
// Reading lifecycle arguments
// =============================================================================

/** Read a decoration instance's argument by field name, applying primary-field
 *  sugar: a single bare positional arg binds the definition's primary. */
export function argValue(
  inst: DecorationInstance,
  decl: DecorationDecl,
  field: Name,
): Value | undefined {
  const primary = primaryFieldOf(decl);
  for (const a of inst.args) {
    if (a.name === undefined && field === primary) return a.value;
    if (a.name === field) return a.value;
  }
  return undefined;
}

/**
 * Read `@deco` / `@deco.field` off ONE node's own decorations.
 *
 * §4.3.1: a declared decoration resolves BEFORE a field of the same name,
 * wherever `@name` appears in getter position — a field value, a description
 * (§4.10.3) or a selector. Each of those axes has its own resolver, so this is
 * the shared reader they layer over their field lookup; without it the rule
 * held in selectors alone and the spec's own example (`@planned.from`) read as
 * a missing field everywhere else.
 *
 * Returns undefined when the node carries no decorations — the overwhelmingly
 * common case, so a plain model pays nothing.
 *
 * Only STATIC values: a decoration argument is written in the text and never
 * moves. `@lifecycle` is not here, and cannot be — it answers for a scrub
 * point, which no literalised value can track.
 */
/**
 * What reading `@path` against a node's decorations produced.
 *
 * The distinction is load-bearing and is why this is not just `Value |
 * undefined`: "this name is not a decoration" must fall through to the field
 * axis, while "it IS a decoration, and there is nothing at that argument" must
 * stop there and answer nothing. Collapsing the two makes a field the spec
 * reports as unreachable (§4.3.1) reachable again whenever the decoration
 * happens not to answer — and it made the field axis disagree with the
 * selector axis about the very same getter.
 */
export type DecorationRead =
  | { readonly hit: false }
  | { readonly hit: true; readonly value: Value | undefined };

const MISS: DecorationRead = { hit: false };

/**
 * Resolve one getter path against one decoration instance — the whole rule,
 * in one place.
 *
 * Every axis binds its own subject to this: the field/description axes through
 * `decorationLookup` below, the selector axis through `expression-eval`'s
 * `decorationGetter`. They differ only in how they find the instance
 * (`ElementNode.decorations` is a Map, a resolved declaration carries a list),
 * which is why the LOOKUP is per-axis and the READING is not.
 */
export function readDecorationPath(
  inst: DecorationInstance | undefined,
  registry: DecorationRegistry | undefined,
  path: readonly string[],
): DecorationRead {
  const head = path[0];
  if (!head || !inst) return MISS;
  // Bare `@deco` is truthy-if-present.
  if (path.length === 1) return { hit: true, value: { kind: "boolean", value: true } };
  if (path.length > 2) return { hit: true, value: undefined };
  // `argValue` owns the primary-field sugar: the single bare positional binds
  // whatever the definition declares FIRST (`from` for the built-ins,
  // `description` for a schema with no fields, §4.9). Reimplementing that rule
  // per call site is how three separate readers each ended up hardcoding `to`.
  const decl = registry?.kinds.get(head) ?? DESCRIPTION_PRIMARY;
  return { hit: true, value: argValue(inst, decl, path[1]!) };
}

export function decorationLookup(
  decorations: readonly DecorationInstance[] | undefined,
  registry: DecorationRegistry | undefined,
): ((path: readonly string[]) => DecorationRead) | undefined {
  if (!decorations?.length) return undefined;
  return (path) =>
    readDecorationPath(
      decorations.find((d) => d.name === path[0]),
      registry,
      path,
    );
}

/** Stand-in for a decoration the registry does not know: no schema fields, so
 *  its primary is `description` (§4.9) — the same answer every other axis
 *  gives, so an undeclared name reads identically in all of them. */
const DESCRIPTION_PRIMARY: DecorationDecl = { name: "", fields: [] };

/** The text of a lifecycle arg, whatever literal form it took. */
export function argText(v: Value | undefined): string | undefined {
  if (!v) return undefined;
  if (v.kind === "string" || v.kind === "identifier") return v.text;
  if (v.kind === "number") return String(v.value);
  if (v.kind === "ref") return v.path.join(".");
  return undefined;
}

/**
 * Build an element's OWN lifecycle window from the decorations written on it —
 * before any containment clamp or type stamping.
 *
 * Bounds come out as ordinals on `timeline`. A lifecycle decoration written with
 * no marker still says something ("planned, date unknown"), so it takes the
 * infinite bound rather than resolving to nothing at all.
 */
export function windowFromDecorations(
  instances: readonly DecorationInstance[],
  registry: DecorationRegistry,
  timeline: Timeline,
): LifecycleWindow {
  let w: LifecycleWindow = LIVE_WINDOW;
  for (const inst of instances) {
    if (!isLifecycleDecoration(inst.name)) continue;
    const decl = registry.kinds.get(inst.name);
    if (!decl) continue;
    const from = resolveArg(inst, decl, "from", timeline);
    if (inst.name === PLANNED) {
      w = { ...w, bornFrom: from ?? Number.POSITIVE_INFINITY };
    } else {
      const to = resolveArg(inst, decl, "to", timeline);
      w = {
        ...w,
        // No `from` on a deprecation means it is deprecated already — the sunset
        // is not scheduled, it has begun.
        deadFrom: from ?? Number.NEGATIVE_INFINITY,
        ...(to !== undefined ? { deadTo: to } : {}),
      };
    }
  }
  return w;
}

function resolveArg(
  inst: DecorationInstance,
  decl: DecorationDecl,
  field: Name,
  timeline: Timeline,
): number | undefined {
  const text = argText(argValue(inst, decl, field));
  return text === undefined ? undefined : ordinalOf(timeline, text);
}

/** Every marker a set of decorations mentions, in written order — the raw
 *  material `buildTimeline` sorts into an axis. */
export function markersFromDecorations(
  instances: readonly DecorationInstance[] | undefined,
  registry: DecorationRegistry,
  out: string[],
): void {
  for (const inst of instances ?? []) {
    if (!isLifecycleDecoration(inst.name)) continue;
    const decl = registry.kinds.get(inst.name);
    if (!decl) continue;
    for (const field of ["from", "to"] as const) {
      const text = argText(argValue(inst, decl, field));
      if (text !== undefined && text.trim()) out.push(text.trim());
    }
  }
}

/** Qualified-name helper mirroring the resolver's space-qualified keys. */
export function decorationId(space: QualifiedName | undefined, name: Name): string {
  return (space ? space.join(".") : "") + "::" + name;
}
